import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  DietLog,
  deleteDietLog,
  listDietLogs,
} from "../../services/DietLogService";
import { capitalizeFirstLetter } from "../../utils/commons";
import { ConfirmationDialog } from "../../utils/ConfirmationDialog";
import DateRangePicker from "../../components/DateRangePicker";
import DietLogCard from "./DietLogCard";

interface DietLogsDrawerProps {
  open: boolean;
  userId: string;
  onClose: () => void;
}

/* ─── Drawer: a member's diet logs (date-filtered, paginated) ──── */

const DietLogsDrawer: React.FC<DietLogsDrawerProps> = ({
  open,
  userId,
  onClose,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<DietLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const [pendingDelete, setPendingDelete] = useState<DietLog | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const pageData = await listDietLogs({
        userId,
        page,
        size: rowsPerPage,
        startDate: range.startDate || undefined,
        endDate: range.endDate || undefined,
      });
      setRows(pageData?.content ?? []);
      setTotal(pageData?.totalElements ?? 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage, range.startDate, range.endDate]);

  // Stable identity so the memoized cards don't all re-render on save; only
  // the one changed `log` object is replaced, keeping the others' identity.
  const handleReviewed = useCallback((updated: DietLog) => {
    setRows((prev) =>
      prev.map((row) => (row.id === updated.id ? updated : row))
    );
  }, []);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteDietLog(pendingDelete.id);
      enqueueSnackbar("Diet log deleted", { variant: "success" });
      if (rows.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        fetchRows();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.data ||
        error?.response?.data?.message ||
        "Failed to delete diet log";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setPendingDelete(null);
    }
  };

  const handleRangeChange = (next: { startDate: string; endDate: string }) => {
    setPage(0);
    setRange(next);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 520 },
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6">Diet Logs</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <DateRangePicker
          startDate={range.startDate}
          endDate={range.endDate}
          onChange={handleRangeChange}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            py={6}
          >
            <RestaurantOutlinedIcon
              sx={{ fontSize: 36, color: theme.palette.text.secondary }}
            />
            <Typography variant="body2" color="text.secondary">
              No diet logs
              {range.startDate || range.endDate
                ? " for the selected dates."
                : " submitted yet."}
            </Typography>
          </Box>
        ) : (
          rows.map((row) => (
            <DietLogCard
              key={row.id}
              log={row}
              onReviewed={handleReviewed}
              onRequestDelete={setPendingDelete}
              onOpenPhoto={setPhotoUrl}
            />
          ))
        )}
      </Box>

      {!loading && total > 0 && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Box>
      )}

      {/* Full-size plate lightbox */}
      <Dialog
        open={Boolean(photoUrl)}
        onClose={() => setPhotoUrl(null)}
        maxWidth="md"
      >
        {photoUrl && (
          <Box
            component="img"
            src={photoUrl}
            alt="plate"
            sx={{ display: "block", maxWidth: "100%", maxHeight: "85vh" }}
          />
        )}
      </Dialog>

      <ConfirmationDialog
        open={Boolean(pendingDelete)}
        heading="Delete diet log"
        description={
          pendingDelete
            ? `Delete the ${capitalizeFirstLetter(
                pendingDelete.category.toLowerCase()
              )} plate logged ${dayjs(pendingDelete.loggedAt).format(
                "MMM D, YYYY"
              )}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        onProceed={handleDelete}
        handleClose={() => setPendingDelete(null)}
      />
    </Drawer>
  );
};

export default DietLogsDrawer;
